import { API_URL } from '../../constants'
import { useEffect, useState } from 'react'
import { fetchWithLanguage } from '../../util/fetchFunction'
import { FaInstagram, FaTiktok, FaXTwitter, FaFacebookF, FaYoutube } from 'react-icons/fa6'
import { FaSnapchat } from 'react-icons/fa'
import { languageUtil } from '../../util/languageUtils'
import { useTranslation } from 'react-i18next'

export default function Footer() {
    const [footer, setFooter] = useState({})
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState()
    const { t } = useTranslation()
    const { language } = languageUtil()
    const rtl = language === 'ar'

    const navMenus = [
        {
            buttonText: t('navigation.menuItems.news'),
            href: '/news',
        },
        {
            buttonText: t('navigation.menuItems.projects'),
            href: '/projects',
        },
        {
            buttonText: t('navigation.menuItems.religious_dues'),
            href: '/religious-dues',
        },
        {
            buttonText: t('navigation.menuItems.get_involved'),
            href: '/get-involved',
        },
        {
            buttonText: t('navigation.menuItems.about_us'),
            href: '/about-us',
        },
    ]

    useEffect(() => {
        const fetchContact = async () => {
            await fetchWithLanguage('platform-information', setFooter, setError, setLoading, {
                transform: item => ({
                    name: rtl ? item.platform_name_ar : item.platform_name_en,
                    description: rtl ? item.platform_description_ar : item.platform_description_en,
                    logo: item.footer_logo,
                    bank: {
                        name: item.bank_name,
                        holder: item.account_holder,
                        number: item.account_number,
                    },
                    socials: {
                        tiktok: item.tiktok,
                        instagram: item.instagram,
                        snapchat: item.snapchat,
                        x: item.x,
                        facebook: item.facebook,
                        youtube: item.youtube,
                    },
                }),
            })
        }

        fetchContact()
    }, [language])

    return (
        <footer className='px-6 py-12 text-gray-200 bg-zinc-900'>
            <div className='grid gap-12 mx-auto max-w-7xl sm:grid-cols-2 lg:grid-cols-4'>
                {/* Logo + Description */}
                <div className='space-y-4'>
                    <a href='/' className='text-lg font-bold text-yellow-700'>
                        <img src='/logo-white.png' className='w-24 h-7' alt='logo of strive2help' />
                    </a>
                    {footer?.description && <p className='text-sm'>{footer.description}</p>}
                </div>

                {/* Navigation */}
                <div>
                    <h3 className='mb-4 text-lg font-semibold'>{t('footer.navigation')}</h3>
                    <ul className='space-y-2'>
                        {navMenus.map((menu, index) => (
                            <li key={index}>
                                <a href={menu.href} className='hover:text-white'>
                                    {menu.buttonText}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Bank Info */}
                <div>
                    <h3 className='mb-4 text-lg font-semibold'>{t('footer.bank')}</h3>
                    <ul className='space-y-2 text-sm'>
                        <li className='p-3 border border-gray-700 rounded-lg bg-zinc-800/50'>
                            <div className='font-medium text-gray-100'>Barclays Bank</div>
                            <span className='font-medium text-yellow-600'>STRIVE 2 HELP</span>
                            <div className='mt-1 text-gray-400'>
                                {t('footer.accountNo')}23151891
                            </div>
                            <div className='mt-1 text-gray-400'>{t('footer.sortCode')}20-76-92</div>
                            {/* <div className='mt-1 text-gray-400'>
                                {t('footer.iban')}GB05BUKB20769223151891
                            </div>
                            <div className='mt-1 text-gray-400'>
                                {t('footer.swiftCode')}BUKBGB22
                            </div> */}
                        </li>
                    </ul>
                    <img
                        src='/fundraising-logo.png'
                        className='w-48 h-16 mt-6'
                        alt='logo of strive2help'
                    />
                </div>

                {/* Socials */}
                <div>
                    <h3 className='mb-4 text-lg font-semibold'>Follow Us</h3>
                    <div className='flex flex-wrap gap-4 text-xl'>
                        {footer?.socials?.instagram && (
                            <a
                                href={footer.socials.instagram}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                <FaInstagram />
                            </a>
                        )}
                        {footer?.socials?.tiktok && (
                            <a
                                href={footer.socials.tiktok}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                <FaTiktok />
                            </a>
                        )}
                        {footer?.socials?.snapchat && (
                            <a
                                href={footer.socials.snapchat}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                <FaSnapchat />
                            </a>
                        )}
                        {footer?.socials?.x && (
                            <a href={footer.socials.x} target='_blank' rel='noopener noreferrer'>
                                <FaXTwitter />
                            </a>
                        )}
                        {footer?.socials?.facebook && (
                            <a
                                href={footer.socials.facebook}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                <FaFacebookF />
                            </a>
                        )}
                        {footer?.socials?.youtube && (
                            <a
                                href={footer.socials.youtube}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                <FaYoutube />
                            </a>
                        )}
                    </div>
                </div>

                {/* Home Address + Bank */}
                {/* <div className='space-y-4'>
                    <h3 className='mb-4 text-lg font-semibold'>{t('footer.homeAddress')}</h3>
                    <p className='text-sm'>
                        51 Grimesthorpe Road <br />
                        Sheffield, <br />
                        S4 7HE
                    </p>
                </div> */}

                {/* Charity Address */}
                <div className='space-y-4'>
                    <h3 className='mb-4 text-lg font-semibold'>{t('footer.charityAddress')}</h3>
                    <p className='text-sm'>
                        Pennine Five, Block 2 <br />
                        1 Tenter St <br />
                        Sheffield <br />
                        S1 4BY
                    </p>
                </div>

                {/* Newsletter */}
                <div className='col-span-2'>
                    <NewsletterForm />
                </div>
            </div>

            {/* Bottom Line */}
            <div className='flex flex-col items-center gap-1 pt-6 mt-12 text-xs text-center text-gray-500 border-t border-gray-700 sm:gap-2 sm:text-sm'>
                <span>{t('footer.copyright')}</span>
                <span>{t('footer.registeredCharity')}</span>
                <span>
                    <a
                        href='https://thepalmsolutions.com/'
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-gray-500 transition hover:text-yellow-600'
                    >
                        {t('footer.madeWith')}
                    </a>
                </span>
            </div>
        </footer>
    )
}

function NewsletterForm() {
    const [email, setEmail] = useState('')
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState('')
    const { t } = useTranslation()

    const handleSubmit = async e => {
        e.preventDefault()
        setLoading(true)
        setMessage('')

        try {
            const res = await fetch(`${API_URL}/newsletter/subscribe`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept-Language': 'en',
                },
                body: JSON.stringify({ email }),
            })

            if (res.ok) {
                setMessage('Thanks for subscribing!')
                setEmail('')
            } else {
                setMessage('Something went wrong. Please try again.')
            }
        } catch (err) {
            setMessage('Network error. Please try again.')
        }

        setLoading(false)
    }

    return (
        <div className='max-w-md'>
            <h3 className='mb-4 text-lg font-semibold text-white'>{t('footer.stayConnected')}</h3>
            <form onSubmit={handleSubmit} className='space-y-2'>
                <label htmlFor='newsletter' className='block text-sm text-white'>
                    {t('footer.subscribe')}
                </label>
                <div className='flex flex-col gap-2 sm:flex-row'>
                    <input
                        type='email'
                        id='newsletter'
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder={t('footer.yourEmail')}
                        className='w-full px-4 py-2 text-white placeholder-gray-400 rounded bg-zinc-800 sm:rounded-l sm:rounded-r-none focus:outline-none focus:ring-2 focus:ring-yellow-500'
                        required
                    />
                    <button
                        type='submit'
                        disabled={loading}
                        className='px-4 py-2 text-white bg-yellow-500 rounded sm:rounded-r sm:rounded-l-none hover:bg-yellow-600 disabled:opacity-50'
                    >
                        {loading ? 'Submitting...' : `${t('footer.subscribeButton')}`}
                    </button>
                </div>
                {message && <p className='mt-1 text-sm text-gray-300'>{message}</p>}
            </form>
        </div>
    )
}
